import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { getUserProfile, getTeamAllSessions } from '../../firebase';
import { FiArrowLeft } from 'react-icons/fi';
import { getStrengthColor, getStrengthTextColor } from '../../utils/strengthColors';
import MyHeader from "../../components/common/MyHeader";
import PendingRatingsAlert from '../../components/PendingRatingsAlert';

const Members = ({ isAdminView = false }) => {
    const { groupMembers, groupInfo, loading: authLoading } = useAuth();
    const [selectedMember, setSelectedMember] = useState(null);
    const [memberProfiles, setMemberProfiles] = useState({});
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [sessions, setSessions] = useState([]);

    useEffect(() => {
        const fetchMemberProfiles = async () => {
            if (groupMembers && groupMembers.length > 0) {
                setLoading(true);
                const profiles = {};

                try {
                    await Promise.all(
                        groupMembers.map(async (member) => {
                            try {
                                const profile = await getUserProfile(member.email);
                                profiles[member.email] = profile;
                            } catch (error) {
                                console.error(`Error fetching profile for ${member.email}:`, error);
                                profiles[member.email] = null;
                            }
                        })
                    );
                    setMemberProfiles(profiles);
                } catch (error) {
                    console.error('Error fetching member profiles:', error);
                } finally {
                    setLoading(false);
                }
            } else {
                setLoading(false);
                setMemberProfiles({});
            }
        };

        fetchMemberProfiles();
    }, [groupMembers]);

    const handleMemberClick = (member) => {
        setSelectedMember(member);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedMember(null);
    };

    useEffect(() => {
        async function fetchSessions() {
            if (!groupInfo?.groupId) {
                return;
            }
            try {
                const allSessions = await getTeamAllSessions(groupInfo.groupId);
                setSessions(allSessions);
            } catch (error) {
                console.error("Error fetching team sessions:", error);
            }
        }

        fetchSessions();
    }, [groupInfo]);

    const filteredMembers = groupMembers || [];

    const getInitials = (name) => {
        if (!name) return '?';
        const nameParts = name.trim().split(' ');
        if (nameParts.length === 1) {
            return nameParts[0].charAt(0).toUpperCase();
        }
        return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
    };

    if (authLoading) {
        return (
            <Container>
                <LoadingMessage>Loading team members...</LoadingMessage>
            </Container>
        );
    }

    if (!groupMembers || groupMembers.length === 0) {
        return (
            <Container>
                <MyHeader
                    alertComponent={!isAdminView ? <PendingRatingsAlert sessions={sessions} compact /> : null}
                />
                <ContentSection>
                    <div>No team members found</div>
                </ContentSection>
            </Container>
        );
    }

    return (
        <>
            <MyHeader
                alertComponent={!isAdminView ? <PendingRatingsAlert sessions={sessions} compact /> : null}
            />

            <PageWrapper>
                <Container>
                    <ContentSection>
                        {loading ? (
                            <LoadingContent>
                                <LoadingMessage>Loading member profiles...</LoadingMessage>
                            </LoadingContent>
                        ) : (
                            <>
                                <MembersCountSection>
                                    <MembersCount>{groupMembers.length} team members</MembersCount>
                                </MembersCountSection>

                                <MembersGrid>
                                    {filteredMembers.map((member, index) => {
                                        const profile = memberProfiles[member.email];
                                        const hasProfile = profile && (
                                            profile.bio ||
                                            member.discord_username ||
                                            member.github_username ||
                                            profile.slack ||
                                            profile.linkedin ||
                                            (profile.strengths && profile.strengths.length > 0)
                                        );

                                        return (
                                            <MemberCard
                                                key={member.email || index}
                                                $hasProfile={hasProfile}
                                                onClick={hasProfile ? () => handleMemberClick(member) : undefined}
                                            >
                                                <MemberAvatar color={member.color || '#745ec5'}>
                                                    {getInitials(member.name)}
                                                </MemberAvatar>
                                                <MemberInfo>
                                                    <MemberName>{member.name || 'Unknown'}</MemberName>
                                                    <PersonalStrengths>
                                                        {profile?.strengths?.slice(0, 3).map((strength, idx) => (
                                                            <StrengthTag
                                                                key={idx}
                                                                color={getStrengthColor(strength)}
                                                                $textColor={getStrengthTextColor(strength)}
                                                            >
                                                                {strength}
                                                            </StrengthTag>
                                                        ))}
                                                    </PersonalStrengths>
                                                    {hasProfile && (
                                                        <ProfileIndicator>
                                                            <span>View More</span>
                                                        </ProfileIndicator>
                                                    )}
                                                </MemberInfo>
                                            </MemberCard>
                                        );
                                    })}
                                </MembersGrid>
                            </>
                        )}
                    </ContentSection>

                    {showModal && selectedMember && (
                        <ModalOverlay onClick={closeModal}>
                            <ModalContent onClick={(e) => e.stopPropagation()}>
                                <ModalHeaderSection>
                                    <BackButtonModal onClick={closeModal}>
                                        <FiArrowLeft size={20} />
                                        <span>Back</span>
                                    </BackButtonModal>
                                    <ModalTitle>Team Directory</ModalTitle>
                                    <ModalSubtitle>{groupMembers.length} team members</ModalSubtitle>
                                </ModalHeaderSection>

                                <ModalBody>
                                    <MemberDetailCard>
                                        <MemberDetailHeader>
                                            <MemberAvatar color={selectedMember.color || '#745ec5'} size="large">
                                                {getInitials(selectedMember.name)}
                                            </MemberAvatar>
                                            <MemberDetailInfo>
                                                <MemberDetailName>{selectedMember.name || 'Unknown'}</MemberDetailName>
                                                <MemberDetailEmail>{selectedMember.email}</MemberDetailEmail>
                                                <PersonalStrengths>
                                                    {memberProfiles[selectedMember.email]?.strengths?.map((strength, idx) => (
                                                        <StrengthTag
                                                            key={idx}
                                                            color={getStrengthColor(strength)}
                                                            $textColor={getStrengthTextColor(strength)}
                                                        >
                                                            {strength}
                                                        </StrengthTag>
                                                    ))}
                                                </PersonalStrengths>
                                            </MemberDetailInfo>
                                        </MemberDetailHeader>

                                        {memberProfiles[selectedMember.email]?.bio && (
                                            <BioSection>
                                                <BioTitle>About Me</BioTitle>
                                                <BioContent>{memberProfiles[selectedMember.email].bio}</BioContent>
                                            </BioSection>
                                        )}

                                        <ContactSection>
                                            {memberProfiles[selectedMember.email]?.phone && (
                                                <ContactInfo>
                                                    <ContactIconImage src="/images/Phone.png" alt="Phone" />
                                                    <SocialLabel>Phone:</SocialLabel>
                                                    <ContactValue>
                                                        {memberProfiles[selectedMember.email].phone}
                                                    </ContactValue>
                                                </ContactInfo>
                                            )}

                                            {(selectedMember?.discord_username ||
                                                selectedMember?.github_username ||
                                                memberProfiles[selectedMember.email]?.slack ||
                                                memberProfiles[selectedMember.email]?.linkedin) && (
                                                    <SocialLinks>
                                                        <SocialLinksTitle>Social Media Links</SocialLinksTitle>
                                                        <SocialLinksContainer>
                                                            {selectedMember?.discord_username && (
                                                                <SocialLink>
                                                                    <SocialIconImage src="/images/Discord.png" alt="Discord" />
                                                                    <SocialLabel>Discord:</SocialLabel>
                                                                    <SocialValue>{selectedMember.discord_username}</SocialValue>
                                                                </SocialLink>
                                                            )}

                                                            {selectedMember?.github_username && (
                                                                <SocialLink>
                                                                    <SocialIconImage src="/images/github.svg" alt="GitHub" />
                                                                    <SocialLabel>GitHub:</SocialLabel>
                                                                    <SocialValue>{selectedMember.github_username}</SocialValue>
                                                                </SocialLink>
                                                            )}

                                                            {memberProfiles[selectedMember.email]?.slack && (
                                                                <SocialLink>
                                                                    <SocialIconImage src="/images/Slack.png" alt="Slack" />
                                                                    <SocialLabel>Slack:</SocialLabel>
                                                                    <SocialValue>{memberProfiles[selectedMember.email].slack}</SocialValue>
                                                                </SocialLink>
                                                            )}

                                                            {memberProfiles[selectedMember.email]?.linkedin && (
                                                                <SocialLink>
                                                                    <SocialIconImage src="/images/LinkedIn.png" alt="LinkedIn" />
                                                                    <SocialLabel>LinkedIn:</SocialLabel>
                                                                    <SocialValue>{memberProfiles[selectedMember.email].linkedin}</SocialValue>
                                                                </SocialLink>
                                                            )}
                                                        </SocialLinksContainer>
                                                    </SocialLinks>
                                                )}
                                        </ContactSection>
                                    </MemberDetailCard>
                                </ModalBody>
                            </ModalContent>
                        </ModalOverlay>
                    )}
                </Container>
            </PageWrapper>
        </>
    );
};

const PageWrapper = styled.div`
    background-color: #f8fafc;
    display: flex;
    flex-direction: column;
    padding: 15px 20px;
`;
const Container = styled.div`
    display: flex;
    flex-direction: column;
    padding: 15px 20px 80px;
`;

const ContentSection = styled.div`
    flex: 1;
    width: 100%;
    padding: 0;
`;

const MembersCountSection = styled.div`
    padding: 20px 0 10px 0;
    border-bottom: 1px solid #e1e5e9;
    margin-bottom: 20px;
`;

const MembersCount = styled.h2`
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin: 0;
    font-family: 'Inter', sans-serif;
`;

const MembersGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;
`;

const MemberCard = styled.div`
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    cursor: ${props => props.$hasProfile ? 'pointer' : 'default'};
    transition: all 0.2s ease;
    border: 2px solid transparent;

    &:hover {
        ${props => props.$hasProfile && `
            transform: translateY(-2px);
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
            border-color: #9333ea;
        `}
    }
`;

const MemberAvatar = styled.div`
    width: ${props => props.size === 'large' ? '80px' : '60px'};
    height: ${props => props.size === 'large' ? '80px' : '60px'};
    border-radius: 50%;
    background-color: ${props => props.color};
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: ${props => props.size === 'large' ? '32px' : '24px'};
    font-weight: 600;
    margin: ${props => props.size === 'large' ? '0' : '0 auto 16px auto'};
    font-family: 'Inter', sans-serif;
`;

const MemberInfo = styled.div`
    text-align: center;
`;

const MemberName = styled.h3`
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin: 0 0 12px 0;
    font-family: 'Inter', sans-serif;
`;

const PersonalStrengths = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: center;
    margin-bottom: 12px;
    min-height: 52px;
    align-content: flex-start;
`;

const StrengthTag = styled.span`
    background-color: ${props => props.color};
    color: ${props => props.$textColor || 'white'};
    font-size: 12px;
    padding: 4px 12px;
    border-radius: 16px;
    font-weight: 500;
    font-family: 'Inter', sans-serif;
`;

const ProfileIndicator = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    font-size: 12px;
    color: #666;
    font-family: 'Inter', sans-serif;
    
    span:first-child {
        font-size: 14px;
    }
`;

const LoadingMessage = styled.div`
    text-align: center;
    font-size: 16px;
    color: #666;
    padding: 40px;
    font-family: 'Inter', sans-serif;
`;

const LoadingContent = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
    width: 100%;
`;

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
`;

const ModalContent = styled.div`
    background: white;
    border-radius: 16px;
    max-width: 600px;
    width: 100%;
    max-height: 80vh;
    overflow-y: auto;
    position: relative;
    display: flex;
    flex-direction: column;
`;

const ModalHeaderSection = styled.div`
    background: #9333ea;
    color: white;
    padding: 16px 24px 24px;
    border-radius: 16px 16px 0 0;
`;

const BackButtonModal = styled.button`
    background: none;
    border: none;
    color: white;
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 16px;
    font-family: 'Inter', sans-serif;
    opacity: 0.9;
    margin-bottom: 16px;
    
    &:hover {
        opacity: 1;
    }
`;

const ModalTitle = styled.h1`
    font-size: 24px;
    font-weight: 600;
    margin: 0 0 8px 0;
    font-family: 'Inter', sans-serif;
    text-align: center;
`;

const ModalSubtitle = styled.p`
    font-size: 14px;
    margin: 0;
    opacity: 0.9;
    font-family: 'Inter', sans-serif;
    text-align: center;
`;

const ModalBody = styled.div`
    padding: 24px;
`;

const MemberDetailCard = styled.div`
    background: white;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const MemberDetailHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 24px;
    padding-bottom: 24px;
    border-bottom: 1px solid #e1e5e9;
`;

const MemberDetailInfo = styled.div`
    flex: 1;
`;

const MemberDetailName = styled.h2`
    font-size: 24px;
    font-weight: 600;
    color: #333;
    margin: 0 0 8px 0;
    font-family: 'Inter', sans-serif;
`;

const MemberDetailEmail = styled.p`
    font-size: 16px;
    color: #666;
    margin: 0 0 12px 0;
    font-family: 'Inter', sans-serif;
`;

const BioSection = styled.div`
    margin-bottom: 24px;
    padding-bottom: 24px;
    border-bottom: 1px solid #e1e5e9;
`;

const BioTitle = styled.h3`
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin: 0 0 12px 0;
    font-family: 'Inter', sans-serif;
`;

const BioContent = styled.p`
    font-size: 16px;
    color: #666;
    line-height: 1.6;
    margin: 0;
    font-family: 'Inter', sans-serif;
    white-space: pre-wrap;
    word-break: break-word;
`;

const ContactSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const ContactInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const ContactIconImage = styled.img`
    width: 32px;
    height: 32px;
    object-fit: contain;
`;

const ContactValue = styled.span`
    font-size: 16px;
    color: #333;
    font-family: 'Inter', sans-serif;
`;

const SocialLinks = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const SocialLinksTitle = styled.h3`
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin: 0 0 12px 0;
    font-family: 'Inter', sans-serif;
`;

const SocialLinksContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const SocialLink = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const SocialIconImage = styled.img`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    object-fit: contain;
    flex-shrink: 0;
`;

const SocialLabel = styled.span`
    font-size: 16px;
    font-weight: 500;
    color: #333;
    font-family: 'Inter', sans-serif;
    min-width: 80px;
`;

const EmptyState = styled.span`
    font-size: 14px;
    color: #999;
    font-style: italic;
    font-family: 'Inter', sans-serif;
`;

const SocialValue = styled.span`
    font-size: 14px;
    color: #333;
    font-family: 'Inter', sans-serif;
    word-break: break-all;
`;

export default Members;